import React, { useState, useEffect } from 'react';
import PaymentsAPI from 'api/PaymentsAPI';
import Invoices from 'components/Billing/Invoices';
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import MeterTable from 'components/BiteAPI/MeterTable';
import { Redirect } from 'react-router-dom';
import PayMethodsBox from 'components/Billing/PayMethodsBox';
import UsageMeter from 'components/Billing/UsageMeter';
import CancelPlanModal from '../../components/Plans/CancelPlanModal';
import RenewPlanModal from '../../components/Plans/RenewPlanModal';
import { Modal } from '@mui/material';

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[800],
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#1a90ff',
    ...theme.applyStyles('dark', {
      backgroundColor: '#308fe8',
    }),
  },
}));

interface IBillingOverviewProps {
  cur_user: any;
  team: any;
  handleTabChange: any;
  cusId: string;
}

const BillingOverview: React.FC<IBillingOverviewProps> = ({ cusId, handleTabChange, cur_user, team }) => {
  const [ methods, setMethods ] = useState<any>({ isLoading: true, data: [] });
  const [ invoices, setInvoices ] = useState<any>({ isLoading: true, data: [] });
  const [ cusObj, setCusObj ] = useState<any>({ isLoading: true, data: {} });
  const [ mainSub, setMainSub ] = useState<any>({ isLoading: true, data: {} });
  const [ meterSubs, setMeterSubs ] = useState<any>({ isLoading: true, data: [] });
  const [ defaultChanging, setDefaultChanging ] = useState<boolean>(false);
  const [ billingCancelMode, setBillingCancelMode ] = useState<boolean>(false);
  const [ billingRenewMode, setBillingRenewMode ] = useState<boolean>(false);
  const [ billingSubInfo, setBillingSubInfo ] = useState<any>({});

  if (!cur_user.selected_team?.attributes?.team_admin?.includes(cur_user.basics?.email)) {
    return <Redirect to='/' />;
  }

  const getMainSub = (customer: any) => {
    const meters: any = [];
    if (customer?.data?.subscriptions?.data?.length === 0) {
      setMainSub({ isLoading: false, data: {} });
    }
    customer?.data?.subscriptions?.data?.map((sub: any) => {
      if (!sub.metadata.type || sub.metadata.type !== 'api') {
        setBillingSubInfo(sub);
        sub.items.data.map((item: any) => {
          if (item?.metadata?.type === 'fixed' || item?.metadata?.type === 'trial') {
            setMainSub({ isLoading: false, data: item });
          } else {
            meters.push(item);
          }
        });
        setMeterSubs({ isLoading: false, data: meters });
      }
    });
  };

  const getPaymentMethods = async(teamToQuery: any) => {
    let cusId = '';
    cur_user?.stripe_cus_info?.map((cus: any) => {
      if (cus.name === teamToQuery.name) {
        cusId = cus.id;
        setCusObj({ isLoading: false, data: cus });
      }
    });
    if (cusId) {
      const paymentMethods = await PaymentsAPI.getCustomerPaymentMethods(cusId);
      if (paymentMethods?.success) {
        setMethods({ isLoading: false, data: paymentMethods?.result });
      }
      const cusInvoices = await PaymentsAPI.listCustomerInvoices(cusId);
      if (cusInvoices?.success) {
        setInvoices({ isLoading: false, data: cusInvoices?.result });
      }
    }
  };

  const cancelSubAtPeriodEnd = async(sub_id: string) => {
    const canceled = await PaymentsAPI.cancelSubAtPeriodEnd(sub_id);
    if (canceled?.success) {
      history.go(0);
    }
  };

  const renewSub = async(sub_id: string) => {
    const renewed = await PaymentsAPI.renewCanceledSub(sub_id);
    if (renewed?.success) {
      history.go(0);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!cusObj.isLoading) {
      getMainSub(cusObj);
    }
  }, [ cusObj ]);

  useEffect(() => {
    getPaymentMethods(team);
  }, [ team ]);

  return (
    <div id='billing-page-upper-container'>
      <div className='billing-page-upper-container'>
        <div className='billing-page-header-container'>
          <div className='billing-ovw-plan-container'>
            <div className='billing-ovw-plan-content'>
              {!mainSub.isLoading && !mainSub?.data?.id && (
                <div className='billing-plan-text-container'>
                  <div className='billing-plan-text-summary'>
                    <div className='billing-plan-summary-heading'>
                      <div className='billing-plan-summary-primary-text'>
                        You currently do not have an active plan.
                      </div>
                    </div>
                    <div className='billing-plan-summary-secondary-text'>
                      Start one on the Plans page or click below.
                    </div>
                  </div>
                </div>
              )}
              {!mainSub.isLoading && mainSub?.data?.id && (
                <>
                  <div className='billing-plan-text-container'>
                    <div className='billing-plan-text-summary'>
                      <div className='billing-plan-summary-heading'>
                        {mainSub.data.metadata.type === 'trial' ? (
                          <div className='billing-plan-summary-primary-text'>
                            {mainSub.data.metadata.moduleName} Free Trial x{mainSub.data.quantity}
                          </div>
                        ) : (
                          <div className='billing-plan-summary-primary-text'>
                            {mainSub.data.metadata.moduleName} x{mainSub.data.quantity}
                          </div>
                        )}
                      </div>
                      <div className='billing-plan-summary-secondary-text'>
                        This is your account plan.
                      </div>
                    </div>
                    <div className='billing-price-summary'>
                      <div className='billing-price-dollar-wrap'>
                        <div className='billing-price-dollar-text'>
                          $
                        </div>
                      </div>
                      {mainSub.data.metadata.type === 'trial' ? (
                        <div className='billing-main-price-text'>
                          0
                        </div>
                      ) : (
                        <div className='billing-main-price-text'>
                          {(mainSub.data.quantity * mainSub.data.plan.amount) / 100}
                        </div>
                      )}
                      <div className='billing-price-interval-wrap'>
                        <div className='billing-price-interval-text'>
                          per {mainSub.data.plan.interval}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='billing-progress-seats-bar'>
                    <div className='billing-progress-seats-text'>
                      {cur_user.selected_team.members.length} of {mainSub.data.quantity} seats used
                    </div>
                  </div>
                  <div className='billing-seats-bar-container'>
                    <BorderLinearProgress
                      variant='determinate'
                      value={cur_user.selected_team.members.length / mainSub.data.quantity * 100}
                    />
                  </div>
                </>
              )}
            </div>
            <div className='billing-ovw-plan-footer'>
              <div className='billing-ovw-plan-footer-divider'>
                <svg xmlns='http://www.w3.org/2000/svg' width='463' height='1' viewBox='0 0 463 1' fill='none'>
                  <path fillRule='evenodd' clipRule='evenodd' d='M463 1H0V0H463V1Z' fill='#2E3036'/>
                </svg>
              </div>
              <div className='billing-ovw-plan-footer-content'>
                <div className='billing-ovw-plan-footer-actions'>
                  {billingSubInfo.cancel_at_period_end === false ? (
                    <div className='billing-ovw-plan-footer-cancel-btn' onClick={() => setBillingCancelMode(true)}>
                      <div className='billing-ovw-plan-footer-cancel-text'>
                        Cancel plan
                      </div>
                    </div>
                  ) : (
                    <div className='billing-ovw-plan-footer-cancel-btn' onClick={() => setBillingRenewMode(true)}>
                      <div className='billing-ovw-plan-footer-cancel-text'>
                        Renew plan
                      </div>
                    </div>
                  )}
                  <div className='billing-ovw-plan-footer-manage-btn'
                    onClick={() => {
                      handleTabChange('Plans');
                    }}
                  >
                    <div className='billing-ovw-plan-footer-cancel-text'>
                      Manage plan
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <PayMethodsBox
            setDefaultChanging={setDefaultChanging}
            methods={methods}
            cusId={cusId}
            cusObj={cusObj}
          />
        </div>
        {/* Internal Screening API Usage */}
        <UsageMeter
          mainSub={mainSub}
          meterSubs={meterSubs}
          handleTabChange={handleTabChange}
          cusId={cusId}
          team={team}
        />
        {/* External API Subs Table */}
        {Object.keys(cusObj.data).length > 0 && <MeterTable handleTabChange={handleTabChange} stripeCus={cusObj.data} />}
      </div>
      <Invoices invoices={invoices.data} />
      <Modal
        open={billingCancelMode}
        onClose={() => setBillingCancelMode(false)}
      >
        <CancelPlanModal
          cancelSub={cancelSubAtPeriodEnd}
          subInfo={billingSubInfo}
          setCancelMode={setBillingCancelMode}
        />
      </Modal>
      <Modal
        open={billingRenewMode}
        onClose={() => setBillingRenewMode(false)}
      >
        <RenewPlanModal renewSub={renewSub} subInfo={billingSubInfo} setRenewMode={setBillingRenewMode} />
      </Modal>
    </div>
  );
};

export default BillingOverview;
